import React, { useCallback, useMemo } from 'react';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { useAdHocRecipients } from '../../../lib/services/communication.service';
import { useDebouncedSearch } from '../../../lib/hooks/use-debounced-search';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { ComposeContextRowType, IComposeContextSelection, SportTerminology } from '../../../lib/types';
import { IPaginatedContextRowProps } from './context-selector.types';
import { buildAudienceFromSelections } from '../AudienceSelector/audience-selector.helpers';
import { getMessages } from './context-selector.messages';
import { ContextRow } from './context-row.component';
import { MisfitHelper } from './context-selector.styles';

// Feeds the generic row from a server-searched, paged source (Player today):
// debounced search options scoped by the other rows + fit-check misfit flags.
export const PaginatedContextRow: React.FC<IPaginatedContextRowProps> = ({
  type,
  selections,
  contexts,
  readOnly,
  assessmentId,
  skipOptionQueries,
  onSelectionsChange,
  onTypeChange,
  onEdit,
  onApprove,
  onCancel,
  onRemove
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);

  const { search, onSearchInput, resetSearch } = useDebouncedSearch();

  // A pick or chip removal clears the live search so the next dropdown open
  // lists the full scoped audience, not the subset the last query filtered to.
  const handleSelectionsChange = useCallback((
    rowType: ComposeContextRowType,
    rows: IComposeContextSelection[]
  ) => {
    resetSearch();
    onSelectionsChange(rowType, rows);
  }, [onSelectionsChange, resetSearch]);

  // Options come from the audience the OTHER rows already narrow to (an age
  // group's players, a session's roster) — the intersection the send uses.
  const scopingCriteria = useMemo(
    () => buildAudienceFromSelections(contexts.filter((row) => row.type !== type)),
    [contexts, type]
  );

  const { items, loading } = useAdHocRecipients({
    assessmentId,
    criteria: scopingCriteria,
    search,
    skip: skipOptionQueries
  });

  const options = useMemo(
    () => items.map((player) => ({ key: player.playerAssessmentId, label: player.playerName })),
    [items]
  );

  const selectedIds = useMemo(
    () => selections.map((row) => row.id as string),
    [selections]
  );

  // Fit check: intersecting the selected ids with the scoping criteria returns
  // exactly the selections that still fit — the rest get misfit flags.
  const fitCriteria = useMemo(
    () => ({ ...scopingCriteria, playerAssessmentIds: selectedIds }),
    [scopingCriteria, selectedIds]
  );
  const { items: fittingItems, loading: fitLoading, error: fitError } = useAdHocRecipients({
    assessmentId,
    criteria: fitCriteria,
    limit: selectedIds.length || 1,
    skip: skipOptionQueries || !selectedIds.length
  });
  // A failed fit query returns empty — indeterminate, not "nothing fits" — so it
  // flags nothing rather than marking every valid selection as no-longer-fitting.
  const misfitKeys = useMemo(() => {
    if (fitLoading || fitError || !selectedIds.length) return new Set<string>();
    const fitting = new Set(fittingItems.map((item) => item.playerAssessmentId));
    return new Set(selectedIds.filter((id) => !fitting.has(id)));
  }, [fitLoading, fitError, selectedIds, fittingItems]);

  const helper = misfitKeys.size > 0
    ? (
      <MisfitHelper>
        <IntlMsg
          {...messages.playersNoLongerFit}
          values={{
            count: misfitKeys.size,
            playerWord: pluralize(misfitKeys.size, termLookup[SportTerminology.ATHLETE].toLowerCase()),
            verbWord: pluralize(misfitKeys.size, 'meets', 'meet')
          }}
        />
      </MisfitHelper>
    )
    : undefined;

  return (
    <ContextRow
      type={type}
      selections={selections}
      contexts={contexts}
      readOnly={readOnly}
      options={options}
      loading={loading}
      flaggedKeys={misfitKeys}
      flagTitle="No longer meets the filter criteria"
      helper={helper}
      onSearchInput={onSearchInput}
      onSelectionsChange={handleSelectionsChange}
      onTypeChange={onTypeChange}
      onEdit={onEdit}
      onApprove={onApprove}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  );
};
